import React, { useEffect, createElement } from 'react';
import { ShieldAlertIcon, CpuIcon, MonitorIcon, FileSearchIcon, ActivityIcon, GlobeIcon, LockIcon, ZapIcon, ServerIcon, BarChart3Icon, AlertTriangleIcon, CheckCircle2Icon, DownloadIcon, ArrowRightIcon, ChevronRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function QuasarPlatform() {
  // Set page-specific meta tags
  useEffect(() => {
    // Store original values for cleanup
    const originalTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const ogType = document.querySelector('meta[property="og:type"]');
    const ogImage = document.querySelector('meta[property="og:image"]');
    const ogImageAlt = document.querySelector('meta[property="og:image:alt"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    const twitterImageAlt = document.querySelector('meta[name="twitter:image:alt"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    const origDesc = metaDescription?.getAttribute('content') || '';
    const origKeywords = metaKeywords?.getAttribute('content') || '';
    const origOgTitle = ogTitle?.getAttribute('content') || '';
    const origOgDesc = ogDescription?.getAttribute('content') || '';
    const origOgUrl = ogUrl?.getAttribute('content') || '';
    const origOgType = ogType?.getAttribute('content') || '';
    const origOgImage = ogImage?.getAttribute('content') || '';
    const origOgImageAlt = ogImageAlt?.getAttribute('content') || '';
    const origTwitterTitle = twitterTitle?.getAttribute('content') || '';
    const origTwitterDesc = twitterDescription?.getAttribute('content') || '';
    const origTwitterImage = twitterImage?.getAttribute('content') || '';
    const origTwitterImageAlt = twitterImageAlt?.getAttribute('content') || '';
    const origCanonical = canonical?.getAttribute('href') || '';
    // Update title
    document.title = 'Quasar - Web Engineering & Cyber Defense Platform | COSMO IMAGE AVIATION';
    // Update meta description
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Quasar Web Engineering Platform: Advanced cyber defense solution for real-time threat detection, automated defense playbooks, vulnerability scanning, and AI-powered endpoint integration. Built for military and government cyber squadrons.');
    }
    // Update keywords
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Quasar platform, web engineering, cyber defense, threat detection, vulnerability scanning, DDoS simulation, automated defense, endpoint integration, cybersecurity, military cyber, COSMO IMAGE AVIATION');
    }
    // Update Open Graph tags
    if (ogTitle) ogTitle.setAttribute('content', 'Quasar - Web Engineering & Cyber Defense Platform');
    if (ogDescription) ogDescription.setAttribute('content', 'Advanced cyber defense platform for real-time threat detection, automated defense playbooks, vulnerability scanning, and AI-powered endpoint integration for military and government cyber operations.');
    if (ogUrl) ogUrl.setAttribute('content', 'https://www.cosmoimageaviation.com/quasar');
    if (ogType) ogType.setAttribute('content', 'product');
    if (ogImage) ogImage.setAttribute('content', 'https://cdn.magicpatterns.com/uploads/ogMMqjpkRY984JdTJxFSeg/Screenshot_2026-02-25_222146.png');
    if (ogImageAlt) ogImageAlt.setAttribute('content', 'Quasar Web Engineering Platform Interface');
    // Update Twitter Card tags
    if (twitterTitle) twitterTitle.setAttribute('content', 'Quasar - Web Engineering & Cyber Defense Platform');
    if (twitterDescription) twitterDescription.setAttribute('content', 'Advanced cyber defense platform for real-time threat detection, automated defense playbooks, and AI-powered endpoint integration.');
    if (twitterImage) twitterImage.setAttribute('content', 'https://cdn.magicpatterns.com/uploads/ogMMqjpkRY984JdTJxFSeg/Screenshot_2026-02-25_222146.png');
    if (twitterImageAlt) twitterImageAlt.setAttribute('content', 'Quasar Web Engineering Platform Interface');
    // Update canonical
    if (canonical) canonical.setAttribute('href', 'https://www.cosmoimageaviation.com/quasar');
    // Update og:image dimensions
    const ogImageWidth = document.querySelector('meta[property="og:image:width"]');
    const ogImageHeight = document.querySelector('meta[property="og:image:height"]');
    const origOgImageWidth = ogImageWidth?.getAttribute('content') || '';
    const origOgImageHeight = ogImageHeight?.getAttribute('content') || '';
    if (ogImageWidth) ogImageWidth.setAttribute('content', '1920');
    if (ogImageHeight) ogImageHeight.setAttribute('content', '1080');
    // Add page-specific JSON-LD structured data
    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.id = 'quasar-jsonld';
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Quasar Web Engineering Platform',
      applicationCategory: 'SecurityApplication',
      operatingSystem: 'Web',
      description: 'Advanced cyber defense platform providing real-time threat detection, automated defense playbooks, vulnerability scanning, web intrusion simulation tools, and AI-powered endpoint integration for military and government cyber squadrons.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceCurrency: 'USD',
        seller: {
          '@type': 'Organization',
          name: 'COSMO IMAGE AVIATION PTE. LTD.',
          url: 'https://www.cosmoimageaviation.com/'
        }
      },
      featureList: 'Security Health Radar, Active Threat List, URL Monitoring, Web Intrusion Tools, AI Support & Endpoint Integration, Automated Defense Playbooks',
      screenshot: 'https://cdn.magicpatterns.com/uploads/1D9MxDFKNEeenQvHjPU8AT/Screenshot_2026-02-25_222235.png',
      provider: {
        '@type': 'Organization',
        name: 'COSMO IMAGE AVIATION PTE. LTD.',
        url: 'https://www.cosmoimageaviation.com/'
      }
    });
    document.head.appendChild(jsonLd);
    // Add BreadcrumbList JSON-LD
    const breadcrumbLd = document.createElement('script');
    breadcrumbLd.type = 'application/ld+json';
    breadcrumbLd.id = 'quasar-breadcrumb';
    breadcrumbLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.cosmoimageaviation.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Products',
        item: 'https://www.cosmoimageaviation.com/#services'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: 'Quasar Platform',
        item: 'https://www.cosmoimageaviation.com/quasar'
      }]
    });
    document.head.appendChild(breadcrumbLd);
    // Scroll to top on mount
    window.scrollTo(0, 0);
    // Cleanup on unmount
    return () => {
      document.title = originalTitle;
      if (metaDescription) metaDescription.setAttribute('content', origDesc);
      if (metaKeywords) metaKeywords.setAttribute('content', origKeywords);
      if (ogTitle) ogTitle.setAttribute('content', origOgTitle);
      if (ogDescription) ogDescription.setAttribute('content', origOgDesc);
      if (ogUrl) ogUrl.setAttribute('content', origOgUrl);
      if (ogType) ogType.setAttribute('content', origOgType);
      if (ogImage) ogImage.setAttribute('content', origOgImage);
      if (ogImageAlt) ogImageAlt.setAttribute('content', origOgImageAlt);
      if (ogImageWidth) ogImageWidth.setAttribute('content', origOgImageWidth);
      if (ogImageHeight) ogImageHeight.setAttribute('content', origOgImageHeight);
      if (twitterTitle) twitterTitle.setAttribute('content', origTwitterTitle);
      if (twitterDescription) twitterDescription.setAttribute('content', origTwitterDesc);
      if (twitterImage) twitterImage.setAttribute('content', origTwitterImage);
      if (twitterImageAlt) twitterImageAlt.setAttribute('content', origTwitterImageAlt);
      if (canonical) canonical.setAttribute('href', origCanonical);
      document.getElementById('quasar-jsonld')?.remove();
      document.getElementById('quasar-breadcrumb')?.remove();
    };
  }, []);
  return <div className="min-h-screen w-full bg-white font-sans" data-id="element-262">
      <Header data-id="element-263" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pb-32 px-4 bg-navy overflow-hidden isolate" data-id="element-264">
        {/* Background Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-teal/20 z-0" data-id="element-265"></div>

        {/* Pattern Overlay */}
        <div className="absolute inset-0 opacity-10 z-0" data-id="element-266">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} data-id="element-267"></div>
        </div>

        <div className="container mx-auto max-w-[var(--maxw)] relative z-10" data-id="element-268">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-269">
            <div className="lg:w-1/2" data-id="element-270">
              <div className="inline-block border-l-4 border-teal pl-4 mb-6" data-id="element-271">
                <span className="text-teal font-bold tracking-widest uppercase text-sm" data-id="element-272">
                  WEB ENGINEERING
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight" data-id="element-273">
                Quasar Platform
              </h1>

              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed font-light" data-id="element-274">
                Cyber defense today isn't about building taller walls — it's
                about achieving situational dominance in a digital battlespace
              </p>

              <p className="text-gray-300 text-lg max-w-xl mb-10" data-id="element-275">
                Web Engineering Platform help the squadron spot threats quickly,
                test defenses safely, and respond fast with automated actions.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-276">
                <button onClick={() => {
                const section = document.getElementById('features-section');
                if (section) section.scrollIntoView({
                  behavior: 'smooth'
                });
              }} className="bg-teal hover:bg-teal/90 text-white font-bold py-4 px-8 rounded-none transition-all flex items-center justify-center gap-2 group" data-id="element-277">
                  View Features
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-278" />
                </button>
                <a href="https://drive.google.com/drive/folders/1q38GvvnFWDCRppt3rAnzCdkgT3tmDTrV?usp=sharing" target="_blank" rel="noopener noreferrer" className="bg-white hover:bg-gray-100 text-navy font-bold py-4 px-8 rounded-none transition-all flex items-center justify-center gap-2" data-id="element-279">
                  <DownloadIcon className="w-5 h-5" data-id="element-280" />
                  Download Brochure
                </a>
              </div>
            </div>

            <div className="lg:w-1/2 w-full" data-id="element-281">
              <div className="relative bg-white p-2 shadow-2xl border border-gray-100" data-id="element-282">
                <img src="https://cdn.magicpatterns.com/uploads/ogMMqjpkRY984JdTJxFSeg/Screenshot_2026-02-25_222146.png" alt="Quasar Botnet Architecture Diagram" className="w-full h-auto" data-id="element-283" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* From Threat to Control Section */}
      <section id="features-section" className="py-20 lg:py-24 bg-white" data-id="element-284">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-285">
          <div className="text-center mb-16" data-id="element-286">
            <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-287">
              BENEFITS
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-navy mb-6" data-id="element-288">
              "From Threat to Control—Fast"
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16" data-id="element-289">
            {/* Feature 1 */}
            <div className="flex gap-6" data-id="element-290">
              <div className="flex-shrink-0" data-id="element-291">
                <div className="w-16 h-16 bg-navy text-white flex items-center justify-center rounded-none" data-id="element-292">
                  <FileSearchIcon className="w-8 h-8" data-id="element-293" />
                </div>
              </div>
              <div data-id="element-294">
                <h3 className="text-xl font-bold text-navy mb-3" data-id="element-295">
                  Enhance Situational Awareness
                </h3>
                <ul className="space-y-3 text-gray-600" data-id="element-296">
                  <li className="flex items-start gap-3" data-id="element-297">
                    <ChevronRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" data-id="element-298" />
                    <span data-id="element-299">
                      Deliver real-time visibility of ongoing threats, system
                      health, and attack surfaces.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-300">
                    <ChevronRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" data-id="element-301" />
                    <span data-id="element-302">
                      Enable leadership to make faster, data-driven operational
                      decisions.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="flex gap-6" data-id="element-303">
              <div className="flex-shrink-0" data-id="element-304">
                <div className="w-16 h-16 bg-navy text-white flex items-center justify-center rounded-none" data-id="element-305">
                  <ActivityIcon className="w-8 h-8" data-id="element-306" />
                </div>
              </div>
              <div data-id="element-307">
                <h3 className="text-xl font-bold text-navy mb-3" data-id="element-308">
                  Automate Defense Playbooks
                </h3>
                <ul className="space-y-3 text-gray-600" data-id="element-309">
                  <li className="flex items-start gap-3" data-id="element-310">
                    <ChevronRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" data-id="element-311" />
                    <span data-id="element-312">
                      Replace manual, reactive response with automated,
                      rule-based workflows.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-313">
                    <ChevronRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" data-id="element-314" />
                    <span data-id="element-315">
                      Ensure consistency and precision even under high
                      operational tempo.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="flex gap-6" data-id="element-316">
              <div className="flex-shrink-0" data-id="element-317">
                <div className="w-16 h-16 bg-navy text-white flex items-center justify-center rounded-none" data-id="element-318">
                  <MonitorIcon className="w-8 h-8" data-id="element-319" />
                </div>
              </div>
              <div data-id="element-320">
                <h3 className="text-xl font-bold text-navy mb-3" data-id="element-321">
                  Train and Test Through Realistic Simulations
                </h3>
                <ul className="space-y-3 text-gray-600" data-id="element-322">
                  <li className="flex items-start gap-3" data-id="element-323">
                    <ChevronRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" data-id="element-324" />
                    <span data-id="element-325">
                      Provide a safe environment to simulate DDoS, intrusion,
                      and exploit scenarios.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-326">
                    <ChevronRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" data-id="element-327" />
                    <span data-id="element-328">
                      Strengthen the squadron's readiness through measurable
                      exercises — not theory.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="flex gap-6" data-id="element-329">
              <div className="flex-shrink-0" data-id="element-330">
                <div className="w-16 h-16 bg-navy text-white flex items-center justify-center rounded-none" data-id="element-331">
                  <CpuIcon className="w-8 h-8" data-id="element-332" />
                </div>
              </div>
              <div data-id="element-333">
                <h3 className="text-xl font-bold text-navy mb-3" data-id="element-334">
                  Integrate Intelligence and Action
                </h3>
                <ul className="space-y-3 text-gray-600" data-id="element-335">
                  <li className="flex items-start gap-3" data-id="element-336">
                    <ChevronRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" data-id="element-337" />
                    <span data-id="element-338">
                      Connect data from multiple sensors, endpoints, and threat
                      feeds.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-339">
                    <ChevronRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" data-id="element-340" />
                    <span data-id="element-341">
                      Turn threat intelligence into actionable defense measures,
                      instantly.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Section */}
      <section className="py-20 lg:py-24 bg-gray-50" data-id="element-342">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-343">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-344">
            <div className="lg:w-1/2 w-full" data-id="element-345">
              <div className="bg-white p-2 shadow-2xl border border-gray-100" data-id="element-346">
                <img src="https://cdn.magicpatterns.com/uploads/1D9MxDFKNEeenQvHjPU8AT/Screenshot_2026-02-25_222235.png" alt="Quasar Dashboard Interface" className="w-full h-auto" data-id="element-347" />
              </div>
            </div>
            <div className="lg:w-1/2" data-id="element-348">
              <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-349">
                INTERFACE
              </span>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 text-navy" data-id="element-350">
                Dashboard
              </h2>
              <p className="text-xl text-gray-600 mb-10" data-id="element-351">
                Displays overall security posture and active threats in real
                time
              </p>

              <div className="space-y-8" data-id="element-352">
                <h3 className="text-2xl font-bold text-navy border-b border-gray-200 pb-4" data-id="element-353">
                  Key Features:
                </h3>
                <ul className="space-y-4" data-id="element-354">
                  <li className="flex items-start gap-4" data-id="element-355">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-356" />
                    <span className="text-lg text-gray-700" data-id="element-357">
                      <strong className="text-navy" data-id="element-358">
                        Security Health Radar
                      </strong>{' '}
                      — visual summary of five key security domains
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-359">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-360" />
                    <span className="text-lg text-gray-700" data-id="element-361">
                      <strong className="text-navy" data-id="element-362">Active Threat List</strong>{' '}
                      — ongoing incidents with severity, affected systems, and
                      recommendations
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-363">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-364" />
                    <span className="text-lg text-gray-700" data-id="element-365">
                      <strong className="text-navy" data-id="element-366">
                        Threat Summary Metrics
                      </strong>{' '}
                      — total threats, active threats, protected endpoints
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* URL Monitoring Section */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-367">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-368">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-20" data-id="element-369">
            <div className="lg:w-1/2 w-full" data-id="element-370">
              <div className="bg-white p-2 shadow-2xl border border-gray-100" data-id="element-371">
                <img src="https://cdn.magicpatterns.com/uploads/18dQXhhiBPLeU6jCft4C47/Screenshot_2026-02-25_222243.png" alt="URL Monitoring Interface" className="w-full h-auto" data-id="element-372" />
              </div>
            </div>
            <div className="lg:w-1/2" data-id="element-373">
              <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-374">
                SCANNING
              </span>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 text-navy" data-id="element-375">
                URL Monitoring
              </h2>
              <p className="text-xl text-gray-600 mb-10" data-id="element-376">
                Domain-based vulnerability scanning module
              </p>

              <div className="space-y-8" data-id="element-377">
                <h3 className="text-2xl font-bold text-navy border-b border-gray-200 pb-4" data-id="element-378">
                  Key Features:
                </h3>
                <ul className="space-y-4" data-id="element-379">
                  <li className="flex items-start gap-4" data-id="element-380">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-381" />
                    <span className="text-lg text-gray-700" data-id="element-382">
                      URL input and vulnerability scoring (Low—Medium—High)
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-383">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-384" />
                    <span className="text-lg text-gray-700" data-id="element-385">
                      Severity-level breakdown (High / Medium / Low)
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-386">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-387" />
                    <span className="text-lg text-gray-700" data-id="element-388">
                      Port and network service configuration view
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Web Intrusion Tools Section */}
      <section className="py-20 lg:py-24 bg-gray-50" data-id="element-389">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-390">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-391">
            <div className="lg:w-1/2 w-full" data-id="element-392">
              <div className="bg-white p-2 shadow-2xl border border-gray-100" data-id="element-393">
                <img src="https://cdn.magicpatterns.com/uploads/e6rvaAQoeZuREr8cQvqhvr/Screenshot_2026-02-25_222256.png" alt="Web Intrusion Tools Interface" className="w-full h-auto" data-id="element-394" />
              </div>
            </div>
            <div className="lg:w-1/2" data-id="element-395">
              <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-396">
                SIMULATION
              </span>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 text-navy" data-id="element-397">
                Web Intrusion Tools
              </h2>
              <p className="text-xl text-gray-600 mb-10" data-id="element-398">
                Controlled environment to simulate and observe attack resilience
              </p>

              <div className="space-y-8 mb-12" data-id="element-399">
                <h3 className="text-2xl font-bold text-navy border-b border-gray-200 pb-4" data-id="element-400">
                  Key Features:
                </h3>
                <ul className="space-y-4" data-id="element-401">
                  <li className="flex items-start gap-4" data-id="element-402">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-403" />
                    <span className="text-lg text-gray-700" data-id="element-404">
                      Live Console — real-time logs of HTTP requests, agents,
                      and response rates
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-405">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-406" />
                    <span className="text-lg text-gray-700" data-id="element-407">
                      Exploit Trends Chart — attack trend visualization over
                      time
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-408">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-409" />
                    <span className="text-lg text-gray-700" data-id="element-410">
                      Network Attack Surface — IP list with vulnerability scores
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-411">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-412" />
                    <span className="text-lg text-gray-700" data-id="element-413">
                      Layer 7 Application Level Flooding
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-414">
                    <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-415" />
                    <span className="text-lg text-gray-700" data-id="element-416">
                      Automatic Bypass Cloudflare
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-10 border-t border-gray-200" data-id="element-417">
            <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-418">Limitations:</h3>
            <ul className="space-y-4" data-id="element-419">
              <li className="flex items-start gap-4" data-id="element-420">
                <AlertTriangleIcon className="w-6 h-6 text-teal flex-shrink-0 mt-1" data-id="element-421" />
                <span className="text-lg text-gray-700" data-id="element-422">
                  Websites protected by DDOS Protection like Imperva, Ddos
                  Guard, Akamai, Arbour, F5 and others cannot be bypassed
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-423">
                <AlertTriangleIcon className="w-6 h-6 text-teal flex-shrink-0 mt-1" data-id="element-424" />
                <span className="text-lg text-gray-700" data-id="element-425">
                  The system is not designed to attack industry giants who has
                  high security protection such as Facebook / Meta Group,
                  Google, Twitter (X), Instagram, TikTok, CNN, Detik.com, or
                  online media hosted on AWS Cloud, Google Cloud, Alibaba Cloud,
                  and other major hosting providers.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* AI Support & Endpoint Integration Section */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-426">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-427">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-20" data-id="element-428">
            <div className="lg:w-1/2 w-full" data-id="element-429">
              <div className="bg-white p-2 shadow-2xl border border-gray-100" data-id="element-430">
                <img src="https://cdn.magicpatterns.com/uploads/78vsw7TPQuD3G7mENuZGZM/Screenshot_2026-02-25_222308.png" alt="AI Support & Endpoint Integration Interface" className="w-full h-auto" data-id="element-431" />
              </div>
            </div>
            <div className="lg:w-1/2" data-id="element-432">
              <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-433">
                INTEGRATION
              </span>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 text-navy" data-id="element-434">
                AI Support & Endpoint Integration
              </h2>

              <div className="space-y-8 mt-10" data-id="element-435">
                <h3 className="text-2xl font-bold text-navy border-b border-gray-200 pb-4" data-id="element-436">
                  Key Features:
                </h3>
                <ul className="space-y-4" data-id="element-437">
                  <li className="flex items-start gap-4" data-id="element-438">
                    <CheckCircle2Icon className="w-6 h-6 text-teal flex-shrink-0 mt-1" data-id="element-439" />
                    <span className="text-lg text-gray-700" data-id="element-440">
                      AI-driven threat analysis and endpoint coordination
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-441">
                    <CheckCircle2Icon className="w-6 h-6 text-teal flex-shrink-0 mt-1" data-id="element-442" />
                    <span className="text-lg text-gray-700" data-id="element-443">
                      Supports automatic incident correlation and remediation
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-24 bg-gray-50 relative" data-id="element-444">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-navy via-teal to-navy" data-id="element-445"></div>
        <div className="container mx-auto max-w-4xl text-center px-4" data-id="element-446">
          <h2 className="text-3xl md:text-5xl font-bold text-navy mb-8" data-id="element-447">
            Ready to Strengthen Your Cyber Defense?
          </h2>
          <p className="text-xl text-gray-600 mb-12" data-id="element-448">
            Contact us for a demo and further consultation on the Quasar
            platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-449">
            <a href="mailto:contact@cosmoimageaviation.co" className="bg-teal hover:bg-teal/90 text-white font-bold py-4 px-10 rounded-none transition-all shadow-lg" data-id="element-450">
              Contact Us
            </a>
            <a href="https://drive.google.com/drive/folders/1q38GvvnFWDCRppt3rAnzCdkgT3tmDTrV?usp=sharing" target="_blank" rel="noopener noreferrer" className="bg-white border-2 border-navy hover:bg-navy hover:text-white text-navy font-bold py-4 px-10 rounded-none transition-all inline-flex items-center justify-center gap-2" data-id="element-451">
              <DownloadIcon className="w-5 h-5" data-id="element-452" />
              Download Brochure
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-453" />
    </div>;
}